import { createContext, ReactNode, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

if (window && 'scrollRestoration' in window.history) {
	window.history.scrollRestoration = 'manual';
}
/*
 ** context
 */
type Target = { [key: string]: { x: number; y: number; isActive?: boolean } };
interface ScrollPosContext {
	//positions: RefObject<object>;
	stack: string[];
	//setStack: Dispatch<SetStateAction<string[]>>;
	stackInfo: RefObject<Target>;
	setTop: (target: string) => void;
	registerTarget: (target: string) => void;
	unregisterTarget: (target: string) => void;
	setPosition: (target: string, position: { x: number; y: number }) => void;
	getPosition: (target: string) => { x: number; y: number };
}
export const ScrollPosContext = createContext<ScrollPosContext>(null!);

export const ScrollPosProvider = ({ children }: { children: ReactNode }) => {
	const positions = useRef<Target>(JSON.parse(window.sessionStorage.getItem('scroll-pos-storage') || '{}'));
	const [stack, setStack] = useState<string[]>([]);
	const stackInfo = useRef<Target>({});

	const getPosition = useCallback<ScrollPosContext['getPosition']>((target) => positions.current[target] || { x: 0, y: 0 }, []);
	const setPosition = useCallback<ScrollPosContext['setPosition']>((target, position) => {
		positions.current[target] = position;
		window.sessionStorage.setItem('scroll-pos-storage', JSON.stringify(positions.current));
	}, []);

	useEffect(() => {
		function onScroll() {
			// Only save scroll position if this target is currently active (handling window scroll)
			if (stack.length > 0 && stackInfo.current[stack[0]]?.isActive) {
				setPosition(stack[0], { x: window.scrollX, y: window.scrollY });
			}
		}
		window.addEventListener('scroll', onScroll);
		return () => {
			window.removeEventListener('scroll', onScroll);
		};
	}, [setPosition, stack]);

	const setTop = useCallback<ScrollPosContext['setTop']>(
		(target) => {
			setStack((stack) => {
				stackInfo.current[target] = {
					...{ x: 0, y: 0 },
					...((stackInfo.current[target] as object) || {}),
					isActive: true,
				};

				if (stack[0] === target) {
					return stack;
				}

				if (stack.length && stackInfo.current[stack[0]].isActive) {
					stackInfo.current[stack[0]].isActive = false;
					setPosition(stack[0], { x: window.scrollX, y: window.scrollY });
				}

				const newStack = [...stack];
				if (newStack.includes(target)) {
					newStack.splice(newStack.indexOf(target), 1);
				}
				newStack.unshift(target);
				return newStack;
			});
		},
		[setPosition],
	);

	const registerTarget = useCallback<ScrollPosContext['registerTarget']>((target) => {
		setStack((stack) => {
			stackInfo.current[target] = stackInfo.current[target] || {};
			// Preserve existing position from sessionStorage if available
			if (!positions.current[target]) {
				positions.current[target] = { x: 0, y: 0 };
			}
			if (stack.includes(target)) {
				return stack;
			}
			return [...stack, target];
		});
	}, []);

	const unregisterTarget = useCallback<ScrollPosContext['unregisterTarget']>((target) => {
		setStack((stack) => {
			if (stackInfo.current[target]) {
				delete stackInfo.current[target];
			}

			if (!stack.includes(target)) {
				return stack;
			}

			const newStack = [...stack];
			newStack.splice(newStack.indexOf(target), 1);

			if (newStack.length) {
				stackInfo.current[newStack[0]] = { ...(stackInfo.current[newStack[0]] || {}), isActive: true };
			}

			return newStack;
		});
	}, []);

	const context = useMemo(
		() => ({ stack, stackInfo, setTop, registerTarget, unregisterTarget, setPosition, getPosition }),
		[stack, stackInfo, setTop, registerTarget, unregisterTarget, setPosition, getPosition],
	);

	return <ScrollPosContext.Provider value={context}>{children}</ScrollPosContext.Provider>;
};
